import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import {
  CHART_PREF_EVENT,
  loadChartPreferences,
  saveChartPreferences,
  TIMEFRAME_OPTIONS,
  CHART_TYPE_OPTIONS,
} from "utils/chartPreferences";

const CHART_TYPE_HINTS = {
  candle: "Candles",
  line: "Line",
  ohlc: "Bars",
  area: "Area",
};

function SettingsChipGrid({ options, value, onChange, columns = 5 }) {
  return (
    <div
      className="chart-settings-chips"
      style={{ "--chart-settings-cols": columns }}
      role="radiogroup"
    >
      {options.map((opt) => {
        const active = opt.value === value;
        return (
          <button
            key={opt.value}
            type="button"
            role="radio"
            aria-checked={active}
            className={`chart-settings-chip${active ? " chart-settings-chip--active" : ""}`}
            onClick={() => onChange(opt.value)}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

function SettingsSwitch({ label, hint, checked, onChange }) {
  return (
    <label className="chart-settings-switch">
      <span className="chart-settings-switch__text">
        <span className="chart-settings-switch__label">{label}</span>
        {hint ? <span className="chart-settings-switch__hint">{hint}</span> : null}
      </span>
      <span className="chart-settings-switch__track" aria-hidden>
        <input
          type="checkbox"
          checked={Boolean(checked)}
          onChange={(e) => onChange(e.target.checked)}
        />
        <span className="chart-settings-switch__thumb" />
      </span>
    </label>
  );
}

export default function ChartSettingsModal({ onClose, onSaved, darkTheme = false }) {
  const [prefs, setPrefs] = useState(() => loadChartPreferences());

  const themeClass = darkTheme
    ? "platform-menu-modal-overlay--dark"
    : "platform-menu-modal-overlay--light";

  const timeframeOptions = TIMEFRAME_OPTIONS.map((t) => ({ value: t.id, label: t.label }));
  const chartTypeOptions = CHART_TYPE_OPTIONS.map((t) => ({
    value: t.id,
    label: CHART_TYPE_HINTS[t.id] || t.label,
  }));

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  useEffect(() => {
    const onPrefs = (e) => {
      setPrefs(e.detail || loadChartPreferences());
    };
    window.addEventListener(CHART_PREF_EVENT, onPrefs);
    return () => window.removeEventListener(CHART_PREF_EVENT, onPrefs);
  }, []);

  const applyPatch = (patch) => {
    const next = saveChartPreferences(patch);
    setPrefs(next);
  };

  if (typeof document === "undefined") return null;

  return createPortal(
    <div
      className={`modal-overlay chart-settings-overlay chart-settings-overlay--fullpage platform-menu-modal-overlay ${themeClass}`}
      onClick={onClose}
      role="presentation"
    >
      <div
        className="modal-box chart-settings-sheet chart-settings-sheet--fullpage"
        role="dialog"
        aria-modal="true"
        aria-labelledby="chart-settings-title"
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <header className="modal-header chart-settings-header">
          <div className="chart-settings-header__row">
            <div className="chart-settings-header__brand">
              <span className="chart-settings-header__icon" aria-hidden>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M4 19V5M10 19V9M16 19V13M22 19V3"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </svg>
              </span>
              <div className="chart-settings-header__titles">
                <h3 id="chart-settings-title">Chart Settings</h3>
                <p>Customize your trading chart</p>
              </div>
            </div>
            <button
              type="button"
              className="modal-close chart-settings-close"
              onClick={onClose}
              aria-label="Close"
            >
              ✕
            </button>
          </div>
        </header>

        <div className="chart-settings-body">
          <section className="chart-settings-card">
            <div className="chart-settings-card__head">
              <span className="chart-settings-card__title">Default timeframe</span>
              <span className="chart-settings-card__badge">Interval</span>
            </div>
            <SettingsChipGrid
              options={timeframeOptions}
              value={prefs.defaultTimeframe}
              onChange={(defaultTimeframe) => applyPatch({ defaultTimeframe })}
              columns={5}
            />
          </section>

          <section className="chart-settings-card">
            <div className="chart-settings-card__head">
              <span className="chart-settings-card__title">Default chart type</span>
              <span className="chart-settings-card__badge">Style</span>
            </div>
            <SettingsChipGrid
              options={chartTypeOptions}
              value={prefs.defaultChartType}
              onChange={(defaultChartType) => applyPatch({ defaultChartType })}
              columns={4}
            />
          </section>

          <section className="chart-settings-card chart-settings-card--toggles">
            <div className="chart-settings-card__head">
              <span className="chart-settings-card__title">Display options</span>
            </div>
            <SettingsSwitch
              label="Show volume panel"
              hint="Histogram below the price chart"
              checked={prefs.showVolume}
              onChange={(showVolume) => applyPatch({ showVolume })}
            />
            <SettingsSwitch
              label="Crosshair magnet mode"
              hint="Snap crosshair to candle OHLC"
              checked={prefs.crosshairMagnet}
              onChange={(crosshairMagnet) => applyPatch({ crosshairMagnet })}
            />
          </section>
        </div>

        <footer className="chart-settings-footer">
          <button
            type="button"
            className="chart-settings-done"
            onClick={() => {
              onSaved?.();
              onClose();
            }}
          >
            Done
          </button>
        </footer>
      </div>
    </div>,
    document.body
  );
}
